import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Job, JobStatus } from '@/types/admin'
import { Edit, Trash, Eye } from 'lucide-react'

interface JobsTableProps {
  jobs: Job[]
  onEdit: (job: Job) => void
  onDelete: (id: string) => void
}

const statusMap: Record<JobStatus, { label: string; color: string }> = {
  new: { label: 'Nova', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  analyzing: {
    label: 'Em Análise',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
  candidates_sent: {
    label: 'Candidatos Env.',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
  },
  closed: {
    label: 'Fechada',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
  },
}

export function JobsTable({ jobs, onEdit, onDelete }: JobsTableProps) {
  return (
    <div className="rounded-md border border-slate-200 bg-white shadow-sm overflow-hidden">
      <Table>
        <TableHeader className="bg-slate-50">
          <TableRow>
            <TableHead className="font-semibold text-slate-700">
              Escola / Responsável
            </TableHead>
            <TableHead className="font-semibold text-slate-700">
              Cargo
            </TableHead>
            <TableHead className="font-semibold text-slate-700">
              Localização
            </TableHead>
            <TableHead className="font-semibold text-slate-700">
              Contato
            </TableHead>
            <TableHead className="font-semibold text-slate-700">
              Status
            </TableHead>
            <TableHead className="text-right font-semibold text-slate-700">
              Ações
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {jobs.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={6}
                className="text-center py-10 text-slate-500"
              >
                Nenhuma vaga encontrada com os filtros atuais.
              </TableCell>
            </TableRow>
          ) : (
            jobs.map((job) => (
              <TableRow key={job.id} className="hover:bg-slate-50/50">
                <TableCell className="font-medium">
                  <div className="text-slate-900">{job.school_name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                    {job.responsible_name}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="font-medium text-slate-800">
                    {job.position}
                  </div>
                  {job.grade_level && (
                    <div className="text-xs text-slate-500 mt-0.5">
                      {job.grade_level}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="text-sm text-slate-700">{job.city}</div>
                  {job.neighborhood && (
                    <div className="text-xs text-slate-500">
                      {job.neighborhood}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="text-sm font-mono text-slate-600">
                    {job.whatsapp}
                  </div>
                  <div
                    className="text-xs text-slate-500 truncate max-w-[150px]"
                    title={job.email}
                  >
                    {job.email}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={`font-normal ${statusMap[job.status]?.color || ''}`}
                  >
                    {statusMap[job.status]?.label || job.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(job)}
                      title="Ver detalhes / Editar"
                    >
                      <Eye className="h-4 w-4 text-slate-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => onDelete(job.id)}
                      title="Excluir"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}
